
import Header from '@/components/layout/Header.vue';
import Footer from '@/components/layout/Footer.vue';
import TemplateEditor from '@/components/template-builder/TemplateEditor.vue';
import AutomatedScenarioEditor from '@/components/automated-scenarios/AutomatedScenarioEditor.vue';
import { AutomatedScenarioState } from '@/composables/AutomatedScenarios';
import { TemplateEditorState } from '@/composables/template-editor/TemplateEditor';
import StatsEditor from '@/views/stats/StatsEditor.vue';
import {
  computed, ComputedRef, defineComponent, onBeforeMount, onMounted, provide, ref, Ref, watch,
} from 'vue';
import { useStore } from '@/store';
import AppSidebar from '@/components/layout/AppSidebar.vue';
import { UserState, hasAccessToFeatures } from '@/composables/User';
import { Request } from '@/composables/GraphQL';
import Gleap from 'gleap';
import DataViewer from '@/components/data-viewer/DataViewer.vue';
import ChoosePlanV14 from '@/components/my-offer/MyOffer14/ChoosePlanV14.vue';
import ChoosePlanV15 from '@/components/my-offer/MyOffer15/ChoosePlanV15.vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { checkIfDbCreated } from '@/configs/complete';
import gleapActionForm from '@/configs/gleap-actions';
import getShopsDomains from '@/composables/shop/ShopsSendDomains';
import { GleapCustomActionName, GleapChecklistEvent } from '@/types/enums';
import OpenAccessToSupportModal from '@/components/modals/OpenAccessToSupportModal.vue';
import { showToastError } from '@/helpers';
import { getShopPendingRecurringCharges, getTagInstallerLinkRedirect } from '@/composables/user/ShopifyConfig';
import ServicesParametersModal from '@/components/modals/ServicesParametersModal.vue';
import { getMandatoryConfiguredServices } from '@/composables/shop/MyShopParameters';
import { getAchievedTrackingConsentChecklistEvents } from '@/composables/shop/TrackingConsentObjectives';
import { servicesPerGleapChecklist } from '@/configs/services-parameters';
import { getLocalStorageElement } from '@/helpers/LocalStorage';
import ReloadNotice from '@/views/layout/ReloadNotice.vue';
import DataExplorer from '@/components/data-explorer/DataExplorer.vue';
import ImportModal from '@/components/import/ImportModal.vue';
import FlowEditorModal from '@/components/flows/FlowEditorModal.vue';
import ConfirmDialog from 'primevue/confirmdialog';
import type { SpmApiFilter } from '@/components/table-rest-api/filters';
import { useConfirm } from 'primevue/useconfirm';

export default defineComponent({
  name: 'LayoutMain',
  components: {
    DataViewer,
    StatsEditor,
    Footer,
    TemplateEditor,
    AutomatedScenarioEditor,
    AppSidebar,
    Header,
    ChoosePlanV14,
    ChoosePlanV15,
    OpenAccessToSupportModal,
    ServicesParametersModal,
    ReloadNotice,
    DataExplorer,
    ImportModal,
    FlowEditorModal,
    ConfirmDialog,
  },

  setup() {
    // Initialize Gleap chat plugin
    Gleap.setLanguage(UserState.user.lang ?? 'en');
    Gleap.initialize(process.env.VUE_APP_GLEAP_API_KEY || '');
    Gleap.setMaxNetworkRequests(50);
    const {
      locale,
      t,
    } = useI18n();
    const router = useRouter();

    const store = useStore();
    const confirm = useConfirm();

    // Global data source filter — available to any view via inject('globalSourceFilters')
    const globalSourceFilters = computed((): SpmApiFilter[] => {
      const ids: number[] = store.getters['general/getSelectedDataSourceIds'];
      if (!ids || ids.length === 0) return [];
      return [{
        field: 'data_source_id',
        operator: 'IN',
        values: ids.join(','),
      }];
    });
    provide('globalSourceFilters', globalSourceFilters);

    const statsEditorPageId: Ref<number> = computed(() => store.getters['statsEditor/getPageId']);
    const isListImportEditorVisible: Ref<number> = computed(() => store.getters['listImportEditor/getIsEditorVisible']);
    const isDataViewerVisible: Ref<boolean> = computed(() => store.getters['dataViewer/getIsVisible']);
    const isChoosePlanVisible: Ref<boolean> = computed(() => store.getters['choosePlan/getIsVisible']);
    const isServicesParametersModalVisible: Ref<boolean> = computed(() => store.getters['general/getIsServicesParametersModalVisible']);
    const isDataExplorerVisible: Ref<boolean> = computed(() => store.getters['dataExplorer/getIsDataExplorerVisible']);
    const isFlowEditorVisible: Ref<boolean> = computed(() => store.getters['flowEditor/isEditorVisible']);
    const flowVersionConflict = computed(() => store.getters['flowEditor/getVersionConflict']);

    // Watch for flow version conflicts and display modal
    watch(flowVersionConflict, (conflict) => {
      if (conflict) {
        confirm.require({
          group: 'flowVersionConflict',
          message: t('flows.versionConflict.message', { name: conflict.flowName }),
          header: t('flows.versionConflict.header'),
          icon: 'far fa-exclamation-triangle',
          acceptLabel: t('flows.versionConflict.loadRecent'),
          rejectLabel: t('flows.versionConflict.keepLocal'),
          accept: () => {
            // Load from database - close local version and open remote version
            store.dispatch('flowEditor/closeFlow', { id: conflict.flowId, type: conflict.flowType });
            store.dispatch('flowEditor/openFlow', {
              id: conflict.flowId,
              flowData: conflict.remoteFlow,
              type: conflict.flowType,
              name: conflict.flowName,
            });
            store.commit('flowEditor/setVersionConflict', null);
          },
          reject: () => {
            // Keep local version - just restore
            store.dispatch('flowEditor/restoreFlow', { id: conflict.flowId, type: conflict.flowType });
            store.commit('flowEditor/setVersionConflict', null);
          },
        });
      }
    });
    const showReloadNotice = ref(false);
    const shopifyHasPendingRecurringCharge = ref(false);
    const urlShop: Ref<string | null> = ref(UserState.activeShop?.url ?? null);
    const cachedWebsiteContents: ComputedRef<Record<string, string>> = computed(() => store.getters['liveEditor/getWebsiteContents']);
    const refreshKey = ref(0);
    const typeImport = computed(() => store.getters['listImportEditor/getTypeImport']);


    const isMobile = ref(false);
    // eslint-disable-next-line no-restricted-globals
    isMobile.value = screen.width <= 768;

    const bundleVersion = computed(() => Math.max(14, UserState.activeShop?.bundleVersion || 14));

    // Check gleap action
    Gleap.registerCustomAction(async (customAction) => {
      if (customAction.name === 'redirect_shopify_recurring_charge') {
        let shopPendingRecurringCharges = await getShopPendingRecurringCharges(UserState.activeShop?.id ?? 0);
        shopPendingRecurringCharges = shopPendingRecurringCharges && shopPendingRecurringCharges.success ? shopPendingRecurringCharges.charge.urlConfirmation : '';

        if (shopPendingRecurringCharges !== '') {
          window.open(shopPendingRecurringCharges, '_blank');
        } else {
          await showToastError('GENERIC_ERROR');
        }
        return;
      }
      if (customAction.name === 'install_shopify_tag') {
        let tagInstallerLinkRedirect = await getTagInstallerLinkRedirect(UserState.activeShop?.id ?? 0);
        tagInstallerLinkRedirect = tagInstallerLinkRedirect && tagInstallerLinkRedirect.success ? tagInstallerLinkRedirect.url : '';
        if (tagInstallerLinkRedirect !== '') {
          window.open(tagInstallerLinkRedirect, '_blank');
        } else {
          await showToastError('GENERIC_ERROR');
        }
        return;
      }
      if (customAction.name === 'redirect_user_to_bills') {
        await router.push({ name: 'shop.MyOffer' });
        return;
      }
      if (customAction.name === 'open_offer_page') {
        // Check if offer is available
        if ((UserState.activeOffer !== null && Object.keys(UserState.activeOffer).length > 0) || UserState.hasOfferV3) {
          // Reload page to force gleap banner to disappear
          router.go(0);
        } else {
          // Go to offer page
          await router.push({ name: 'shop.MyOffer' });
        }
        return;
      }

      const featurePermission = hasAccessToFeatures(customAction.name);
      const isOfferV3 = UserState.hasOfferV3;

      const doGleapCustomAction = () => {
        if (customAction.name === GleapCustomActionName.GLEAP_OPEN_ACCESS_TO_SUPPORT) {
          store.commit('general/setIsOpenAccessToSupportModalVisible', true);
        } else {
          const gleapAction = gleapActionForm[customAction.name];
          if (gleapAction.isForm) {
            Gleap.startClassicForm(gleapAction.value, true);
          } else if (gleapAction.isLink) {
            window.open(gleapAction.value, '_blank');
          } else if (gleapAction.isPhone) {
            window.open(`tel:${gleapAction.value}`);
          }
        }
        store.commit('general/setGleapAction', null);
      };

      if (isOfferV3) {
        doGleapCustomAction();
      } else if (featurePermission && !featurePermission.access) {
        store.commit('general/setIsFeatureUnavailableModalVisible', true);
        store.commit('general/setFeatureUnavailableMinPlan', featurePermission.minimumPlan);
        store.commit('general/setGleapAction', customAction.name);
      } else if (!featurePermission || (featurePermission && featurePermission.access)) {
        doGleapCustomAction();
      }
    });

    Gleap.on('open', async () => {
      const shopDomains = await getShopsDomains(1, [], ['domain_verified', 'authentication_verified', 'alignment_verified', 'tracking_domain_verified']);
      const configuredServices = await getMandatoryConfiguredServices(UserState.activeShop?.id ?? 0);
      if (shopDomains && shopDomains.length) {
        const domain = shopDomains[0];
        if (domain.domain_verified) {
          Gleap.trackEvent(GleapChecklistEvent.GLEAP_CHECKLIST_VERIFY_DOMAIN);
        }
        if (domain.authentication_verified !== 0) {
          Gleap.trackEvent(GleapChecklistEvent.GLEAP_CHECKLIST_AUTHENTICATE_DOMAIN);
        }
        if (domain.alignment_verified) {
          Gleap.trackEvent(GleapChecklistEvent.GLEAP_CHECKLIST_ALIGN_DOMAIN);
        }
        if (domain.tracking_domain_verified) {
          Gleap.trackEvent(GleapChecklistEvent.GLEAP_CHECKLIST_ALIGN_REDIRECTION_LINKS);
        }
      }
      configuredServices.forEach((configuredService) => {
        Gleap.trackEvent(servicesPerGleapChecklist[configuredService]);
      });
      const trackingConsentEvents = await getAchievedTrackingConsentChecklistEvents();
      trackingConsentEvents.forEach((event) => Gleap.trackEvent(event));
    });

    const checkIfSameShopInUse = () => {
      const activeShopIdFromLocalStorage = getLocalStorageElement('spm_auth_current_shop_id', false, false);

      if ((!UserState.activeShop || UserState.activeShop.id.toString() !== activeShopIdFromLocalStorage) && !UserState.changingShop) {
        showReloadNotice.value = true;
      }
    };

    onMounted(async () => {
      store.dispatch('general/initDataSourceSelection');

      if (urlShop.value && !Object.prototype.hasOwnProperty.call(cachedWebsiteContents.value, urlShop.value)) {
        // Cache homepage of shop's website for using in templateEditor
        const query = 'query ( $url: String) { TemplatesShopWebsiteContent(shopUrl: $url) }';
        const variables = { url: urlShop.value };

        Request<string>({
          name: 'TemplatesShopWebsiteContent',
          query,
          variables,
        }).then((result) => {
          if (result.data) {
            store.commit('liveEditor/setWebsiteContents', { url: urlShop.value, content: result.data });
          }
        });
      }

      if (UserState.activeShop?.solutionEcommerce === 'shopify') {
        const shopPendingRecurringCharges = await getShopPendingRecurringCharges(UserState.activeShop?.id ?? 0);
        shopifyHasPendingRecurringCharge.value = shopPendingRecurringCharges
          && shopPendingRecurringCharges.success
          && shopPendingRecurringCharges.charge
          && shopPendingRecurringCharges.charge.urlConfirmation !== '';
      }

      // Check for changes every 5 seconds
      setInterval(checkIfSameShopInUse, 5000);
    });

    onBeforeMount(async () => {
      store.commit('general/showTheSpinner');
      const dbCreated = await checkIfDbCreated();
      if (!dbCreated) {
        await router.push('/complete');
      }
      store.commit('general/hideTheSpinner');
    });

    watch(locale, () => {
      refreshKey.value += 1;
    });

    // Load minimized flows from localStorage once user is authenticated
    watch(
      [() => UserState.isAuthenticated, () => UserState.activeShop?.id],
      () => {
        if (UserState.isAuthenticated && UserState.activeShop?.id) {
          store.dispatch('flowEditor/loadFromStorage');
        }
      },
      { immediate: true },
    );

    const closeImportModal = () => {
      store.commit('listImportEditor/setIsEditorVisible', false);
    };

    return {
      t,
      TemplateEditorState,
      AutomatedScenarioState,
      statsEditorPageId,
      isListImportEditorVisible,
      isDataViewerVisible,
      isMobile,
      refreshKey,
      isChoosePlanVisible,
      isServicesParametersModalVisible,
      showReloadNotice,
      isDataExplorerVisible,
      isFlowEditorVisible,
      shopifyHasPendingRecurringCharge,
      bundleVersion,
      closeImportModal,
      store,
      UserState,
      typeImport,
    };
  },
});
